import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { 
  Car, Users, MessageSquare, Image as ImageIcon,
  LogOut, PlusCircle, Trash2, ShieldCheck, Edit3, Menu, X, MapPin 
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cars');
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null); 
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Updated Form State to include Branch fields
  const [form, setForm] = useState({
    name: '', category: '', seats: '', rating: 5, description: '', 
    text: '', image: null, 
    title: '', subtitle: '', display_order: 1,
    slug: '', address: '', email: '', phone: '', map_url: '', is_regd_office: false
  });

  const fetchData = useCallback(async () => {
    // Logic: Branches mein bhi display_order use hoga
    const shouldOrder = ['hero_slides', 'cars', 'clients', 'branches'].includes(activeTab);
    
    const { data, error } = await supabase
      .from(activeTab)
      .select('*')
      .order(shouldOrder ? 'display_order' : 'created_at', { 
        ascending: shouldOrder 
      });
      
    if (!error) setDataList(data);
  }, [activeTab]);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) navigate('/admin/login');
    };
    checkUser();
    fetchData();
  }, [activeTab, navigate, fetchData]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this item?")) return;
    try {
      const { error } = await supabase.from(activeTab).delete().eq('id', id);
      if (error) throw error;
      alert("Removed successfully!");
      fetchData();
    } catch (err) { alert(err.message); }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setForm({
      ...form,
      name: item.name || '',
      category: item.category || '',
      seats: item.seats || '',
      description: item.description || '',
      text: item.text || '', 
      title: item.title || '',
      subtitle: item.subtitle || '',
      display_order: item.display_order || 1,
      image_url: item.image_url || item.logo_url || '',
      // Branch specific fields
      slug: item.slug || '',
      address: item.address || '',
      email: item.email || '',
      phone: item.phone || '',
      map_url: item.map_url || '',
      is_regd_office: item.is_regd_office || false
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let publicUrl = editId ? dataList.find(i => i.id === editId).image_url : '';

      // Image handling (Sirf un tabs ke liye jinme images hain)
      if (form.image && ['hero_slides', 'clients', 'cars'].includes(activeTab)) {
        const bucketName = activeTab === 'hero_slides' ? 'hero' : (activeTab === 'clients' ? 'client' : 'car');
        const fileExt = form.image.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const { error: sErr } = await supabase.storage.from(bucketName).upload(fileName, form.image);
        if (sErr) throw sErr;
        const { data: urlData } = supabase.storage.from(bucketName).getPublicUrl(fileName);
        publicUrl = urlData.publicUrl;
      }

      let payload = {}; 

      if (activeTab === 'cars') {
        payload = { name: form.name, category: form.category, seats: parseInt(form.seats), description: form.description, image_url: publicUrl, display_order: parseInt(form.display_order) };
      } else if (activeTab === 'hero_slides') {
        payload = { title: form.title, subtitle: form.subtitle, image_url: publicUrl, display_order: parseInt(form.display_order) };
      } else if (activeTab === 'clients') {
        payload = { name: form.name, logo_url: publicUrl, display_order: parseInt(form.display_order) };
      } else if (activeTab === 'testimonials') {
        payload = { name: form.name, text: form.text };
      } 
      // NEW: Payload for Branches
      else if (activeTab === 'branches') {
        payload = {
          name: form.name,
          slug: form.slug,
          address: form.address,
          email: form.email,
          phone: form.phone,
          map_url: form.map_url,
          display_order: parseInt(form.display_order),
          is_regd_office: form.is_regd_office
        };
      }

      if (editId) {
        const { error } = await supabase.from(activeTab).update(payload).eq('id', editId);
        if (error) throw error;
        alert("Updated Successfully!");
      } else {
        const { error } = await supabase.from(activeTab).insert([payload]);
        if (error) throw error;
        alert("Added Successfully!");
      }

      setEditId(null);
      setForm({ name: '', category: '', seats: '', description: '', text: '', image: null, title: '', subtitle: '', display_order: 1, slug: '', address: '', email: '', phone: '', map_url: '', is_regd_office: false });
      fetchData();
    } catch (err) { alert(err.message); } finally { setLoading(false); }
  };

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden relative">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-[#0a1128] text-white flex flex-col h-full shadow-2xl z-50 transition-transform duration-300 lg:relative lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-white/10 flex justify-between items-center lg:block">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-blue-500" />
            <h1 className="text-xl font-black tracking-tighter uppercase">TravelExpress</h1>
          </div>
          <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden">
            <X size={24} />
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-2 mt-4">
          <TabButton Icon={ImageIcon} label="Hero Slides" active={activeTab === 'hero_slides'} onClick={() => {setActiveTab('hero_slides'); setIsSidebarOpen(false);}} />
          <TabButton Icon={Car} label="Cars Fleet" active={activeTab === 'cars'} onClick={() => {setActiveTab('cars'); setIsSidebarOpen(false);}} />
          <TabButton Icon={Users} label="Our Clients" active={activeTab === 'clients'} onClick={() => {setActiveTab('clients'); setIsSidebarOpen(false);}} />
          <TabButton Icon={MapPin} label="Our Branches" active={activeTab === 'branches'} onClick={() => {setActiveTab('branches'); setIsSidebarOpen(false);}} />
          <TabButton Icon={MessageSquare} label="Testimonials" active={activeTab === 'testimonials'} onClick={() => {setActiveTab('testimonials'); setIsSidebarOpen(false);}} />
        </nav>
        <div className="p-4 border-t border-white/10">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-red-400 font-bold uppercase text-[10px] tracking-widest"><LogOut size={18}/> Logout</button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full w-full overflow-hidden">
        <header className="h-20 bg-white border-b px-4 lg:px-8 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-4">
            <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden text-gray-600">
              <Menu size={24} />
            </button>
            <h2 className="text-xl font-black uppercase">Admin <span className="text-blue-600 italic">Control</span></h2>
          </div>
          <div className="text-[10px] font-bold text-green-500 uppercase tracking-widest flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span> 
              <span>Server Live</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form Section */}
            <div className="lg:col-span-1">
              <div className="bg-white p-4 border border-gray-200 sticky top-6 h-fit shadow-sm">
                <h3 className="font-black uppercase text-xs mb-4 flex items-center gap-2">
                  <PlusCircle size={16}/> {editId ? 'Update' : 'Add New'} {activeTab.replace('_', ' ')}
                </h3>
                <form onSubmit={handleUpload} className="space-y-3">
                  {/* Common Name field for most tabs */}
                  {activeTab !== 'hero_slides' && (
                    <Input label="Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                  )}

                  {activeTab === 'hero_slides' && (
                    <>
                      <Input label="Main Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
                      <Input label="Subtitle" value={form.subtitle} onChange={e => setForm({...form, subtitle: e.target.value})} />
                    </>
                  )}

                  {/* Branch Specific Form Fields */}
                  {activeTab === 'branches' && (
                    <>
                      <Input label="Slug (e.g. chennai)" value={form.slug} onChange={e => setForm({...form, slug: e.target.value.toLowerCase()})} />
                      <Input label="Email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                      <Input label="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                      <Input label="Google Maps URL (Embed)" value={form.map_url} onChange={e => setForm({...form, map_url: e.target.value})} />
                      <div className="space-y-1">
                        <label className="text-[9px] font-bold uppercase text-gray-400">Full Address</label>
                        <textarea className="w-full p-2 bg-gray-50 border text-xs h-20 outline-none focus:border-blue-500" value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
                      </div>
                      <div className="flex items-center gap-2 py-2">
                        <input type="checkbox" checked={form.is_regd_office} onChange={e => setForm({...form, is_regd_office: e.target.checked})} />
                        <label className="text-[10px] font-bold uppercase">Registered Office?</label>
                      </div>
                    </>
                  )}

                  {activeTab === 'cars' && (
                    <>
                      <Input label="Category" value={form.category} onChange={e => setForm({...form, category: e.target.value})} />
                      <Input label="Seats" type="number" value={form.seats} onChange={e => setForm({...form, seats: e.target.value})} />
                      <textarea placeholder="Description" className="w-full p-2 bg-gray-50 border text-xs h-20 outline-none focus:border-blue-500" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
                    </>
                  )}

                  {/* Display Order for tabs that need it */}
                  {['hero_slides', 'cars', 'clients', 'branches'].includes(activeTab) && (
                    <Input label="Display Order" type="number" value={form.display_order} onChange={e => setForm({...form, display_order: e.target.value})} />
                  )}

                  {activeTab === 'testimonials' && (
                    <textarea placeholder="Feedback..." className="w-full p-2 bg-gray-50 border text-xs h-32 outline-none focus:border-blue-500" value={form.text} onChange={e => setForm({...form, text: e.target.value})} />
                  )}

                  {/* Image Upload for specific tabs */}
                  {['hero_slides', 'cars', 'clients'].includes(activeTab) && (
                    <div className="space-y-1">
                      <label className="text-[9px] font-bold uppercase text-gray-400">Media Upload</label>
                      <input type="file" className="text-[10px] w-full" onChange={e => setForm({...form, image: e.target.files[0]})} />
                    </div>
                  )}

                  <button type="submit" className="w-full bg-blue-600 text-white py-3 font-black text-[10px] uppercase tracking-widest mt-2">
                    {loading ? "Processing..." : (editId ? "Update Data" : "Add Data")}
                  </button>
                  {editId && (
                    <button type="button" onClick={() => {setEditId(null); setForm({name:'', title:'', subtitle:'', text:'', category:'', seats:'', description:'', display_order:1, slug:'', address:'', email:'', phone:'', map_url:'', is_regd_office:false})}} className="w-full text-[10px] uppercase font-bold text-gray-400 mt-2">
                      Cancel Edit
                    </button>
                  )}
                </form>
              </div>
            </div>

            {/* List Table Section */}
            <div className="lg:col-span-2 bg-white border overflow-x-auto">
              <table className="w-full text-left min-w-[500px]">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-4 text-[13px] font-black uppercase">Entry Details</th>
                    <th className="p-4 text-[13px] font-black uppercase text-center">Order</th>
                    <th className="p-4 text-[13px] font-black uppercase text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {dataList.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {/* Image logic for tabs with images */}
                          {['hero_slides', 'clients', 'cars'].includes(activeTab) ? (
                            <img src={item.image_url || item.logo_url} className="h-12 w-16 object-cover rounded shadow-sm" alt="" />
                          ) : (
                            <div className="h-10 w-10 bg-blue-100 flex items-center justify-center text-blue-600 rounded">
                               <MapPin size={18} />
                            </div>
                          )}
                          <div>
                            <p className="font-bold text-sm uppercase">{item.title || item.name}</p>
                            <p className="text-[10px] text-gray-500 font-bold uppercase truncate max-w-[200px]">
                              {activeTab === 'branches' ? item.address : (item.category || item.subtitle || 'Entry')}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-center font-bold text-blue-600">{item.display_order || '-'}</td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => startEdit(item)} className="p-2 text-blue-500 hover:bg-blue-50 rounded"><Edit3 size={16}/></button>
                          <button onClick={() => handleDelete(item.id)} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 size={16}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// Sub-components
const TabButton = ({ Icon, label, active, onClick }) => (
  <button onClick={onClick} className={`w-full flex items-center gap-3 px-4 py-3 transition-all ${active ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:bg-white/5'}`}>
    <Icon size={18} /> <span className="font-bold text-sm">{label}</span>
  </button>
);

const Input = ({ label, ...props }) => (
  <div className="space-y-1">
    <label className="text-[9px] font-bold uppercase text-gray-400 ml-1">{label}</label>
    <input {...props} className="w-full bg-gray-50 border p-2 text-xs outline-none focus:border-blue-500 transition-all" />
  </div>
);

export default AdminDashboard;